import { useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import DeviceStatus from "../NewAeps/DeviceStatus";
import VimoAepsForm from "./VimoAepsForm";

const VimoAepsIntegration = () => {

  const { device, captureFingerprint, loading } = useRDService();

  const merchantdetails = useSelector(
    (state) => state.auth?.employeeDetails
  );

  const { lat, lng } = useSelector(
    (state) => state.location
  );

  const [processing, setProcessing] = useState(false);

  const username = merchantdetails?.username;

  const latitude = lat;
  const longitude = lng;

  // ── VIMO Pay transaction endpoints ──────────────────────────────────────────
  const { callApi: balanceEnquiryApi } = useApi(
    "/aeps/vimopay/transaction-from-user",
    "POST"
  );

  const { callApi: cashWithdrawalApi } = useApi(
    "/aeps/vimopay/transaction-from-user",
    "POST"
  );

  const { callApi: otpApi } = useApi(
    "/aeps/vimopay/transaction-otp-from-user",
    "POST"
  );

  const { callApi: miniStatementApi } = useApi(
    "/aeps/vimopay/transaction-from-user",
    "POST"
  );

  const { callApi: aadhaarPayApi } = useApi(
    "/aeps/vimopay/transaction-from-user",
    "POST"
  );
  // ────────────────────────────────────────────────────────────────────────────

  const [popup, setPopup] = useState({
    show: false,
    title: "",
    message: "",
    transactions: null,
    balance: "",
  });

  // Parses VIMO transactionList string into row objects
  const parseMiniStatement = (raw) => {
    if (!raw) return [];
    const str = raw.replace(/^"|"$/g, "").trim();
    const chunks = str.split(/(?=\bTxnType\b)/).filter(s => s.trim().startsWith("TxnType"));
    return chunks.map(chunk => ({
      type: (chunk.match(/TxnType\s+(\S+)/) || [])[1] || "",
      amount: (chunk.match(/TxnAmount\s+([\d.]+)/) || [])[1] || "",
      date: (chunk.match(/TxnDate\s+(\S+)/) || [])[1] || "",
      time: (chunk.match(/TxnTime\s+(\S+)/) || [])[1] || "",
      narration: ((chunk.match(/Narration\s+(.+?)(?=\s*TxnType|$)/) || [])[1] || "").trim(),
    }));
  };

  const [previousData, setPreviousData] = useState(null);
  const [clearFormTrigger, setClearFormTrigger] = useState(0);

  const [otpPopup, setOtpPopup] = useState({
    show: false,
    otp: "",
    cwAuthTxnId: "",
    pendingFormData: null,
    submitting: false,
  });

  // IP address for VIMO
  const [ip, setIp] = useState("");
  const [ipFetched, setIpFetched] = useState(false);
  const getIp = async () => {
    if (ipFetched) return ip;
    try {
      const d = await fetch("https://api.ipify.org?format=json").then(r => r.json());
      setIp(d.ip || "");
      setIpFetched(true);
      return d.ip || "";
    } catch {
      setIpFetched(true);
      return "";
    }
  };

  const deviceType = (device || "mantra").toLowerCase().replace(/[^a-z]/g, "");

  // const WADH_VALUE = "E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=";
  // const WADH_VALUE = "18f4CEiXeXcfGXvgWA/blxD+w2pw7hfQPY45JMytkPw=";

  const closePopup = () => {
    if (popup.show) {
      setClearFormTrigger(prev => prev + 1);
    }
    setPopup({ show: false, title: "", message: "", transactions: null, balance: "", aadhaarNo: "", txnRefId: "" });
  };

  // ── OTP → capture after customer enters OTP (CW/AP > ₹5,000) ──────────────
  const handleOtpSubmit = async () => {
    const formData = otpPopup.pendingFormData;
    try {
      setOtpPopup(p => ({ ...p, submitting: true }));
      setProcessing(true);

      const pidXml = await captureFingerprint("", otpPopup.otp);

      if (!pidXml || !pidXml.includes('errCode="0"')) {
        setOtpPopup({ show: false, otp: "", cwAuthTxnId: "", pendingFormData: null, submitting: false });
        setProcessing(false);
        setPopup({ show: true, title: "Failed", message: "Fingerprint Capture Failed" });
        return;
      }

      const currentIp = await getIp();

      const response = await cashWithdrawalApi({
        mid: username,
        transactionType: formData.txnType,
        amount: String(formData.amount),
        bankIIN: (formData?.bank?.iin || "").replace(/\s+/g, ""),
        ipAddress: currentIp,
        deviceType,
        latitude: latitude?.toString() || "0",
        longitude: longitude?.toString() || "0",
        pidData: pidXml,
        cwAuthTxnId: otpPopup.cwAuthTxnId,
        adhaarNumber: formData.aadhaar,
        mobileNumber: formData.mobile,
      });

      setOtpPopup({ show: false, otp: "", cwAuthTxnId: "", pendingFormData: null, submitting: false });
      setProcessing(false);

      if (response?.status === "000") {
        setPopup({
          show: true,
          title: formData.txnType === "CW" ? "Cash Withdrawal Success" : "Aadhaar Pay Success",
          message:
            `Amount : ₹${response?.transactionAmount}

Avl Balance: ₹${response?.availableBalance}

Bank RRN : ${response?.rrn}
Aadhaar No : ${response?.aadhaarNo}
Date : ${response?.txnDateTime}`
        });
      } else {
        setPopup({
          show: true,
          title: "Failed",
          message: response?.npciMessage
        });
      }
    } catch (err) {
      setOtpPopup(p => ({ ...p, submitting: false }));
      setProcessing(false);
      setPopup({ show: true, title: "Error", message: err?.message || "Transaction Failed" });
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  const handleCapture = async (formData) => {
    try {
      setProcessing(true);
      setPreviousData({ ...formData });

      const currentIp = await getIp();

      // ── OTP gate: CW/AP > ₹5,000 ────────────────────────────────────────
      if (
        (formData.txnType === "CW" || formData.txnType === "AP") &&
        Number(formData.amount) > 5000
      ) {
        const otpTxnType = formData.txnType === "CW" ? "CWTFA" : "APTFA";

        const otpRes = await otpApi({
          mid: username,
          bankIIN: (formData?.bank?.iin || "").replace(/\s+/g, ""),
          transactionType: otpTxnType,
          amount: `${Number(formData.amount).toFixed(4)}`,
          latitude: latitude?.toString() || "0",
          longitude: longitude?.toString() || "0",
          ipAddress: currentIp,
          appPlatform: "android",
          appVersion: "1.0.0",
          adhaarNumber: formData.aadhaar,
          mobileNumber: formData.mobile,
        });

        if (otpRes?.status === "000") {
          setOtpPopup({
            show: true,
            otp: "",
            cwAuthTxnId: otpRes?.txnRefId || "",
            pendingFormData: formData,
            submitting: false,
          });
          setProcessing(false);
          return;
        }
        // statuscode not 000 → OTP not required or failed
        if (otpRes?.status !== "000") {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Failed",
            message: otpRes?.npciMessage
          });
          return;
        }
      }
      // ────────────────────────────────────────────────────────────────────

      const pidXml = await captureFingerprint("");

      if (!pidXml || !pidXml.includes('errCode="0"')) {
        setProcessing(false);
        setPopup({ show: true, title: "Failed", message: "Fingerprint Capture Failed" });
        return;
      }

      const baseRequest = {
        mid: username,
        bankIIN: (formData?.bank?.iin || "").replace(/\s+/g, ""),
        ipAddress: currentIp,
        deviceType,
        latitude: latitude?.toString() || "0",
        longitude: longitude?.toString() || "0",
        pidData: pidXml,
        adhaarNumber: formData.aadhaar,
        mobileNumber: formData.mobile,
      };


      // ── Balance Enquiry ──────────────────────────────────────────────────
      if (formData.txnType === "BE") {
        const response = await balanceEnquiryApi({
          ...baseRequest,
          transactionType: "BE",
          amount: "0",
        });

        if (response?.status === "000") {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Balance Enquiry Success",
            message:
              `Available Balance : ₹${response?.availableBalance}

Aadhaar No : ${response?.aadhaarNo || ""}
Txn Ref ID : ${response?.txnRefId || ""}
Bank IIN : ${response?.bankIIN || formData?.bank?.iin}
Date : ${response?.txnDateTime || ""}`
          });
        } else {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Transaction Failed",
            message: response?.npciMessage
          });
        }
      }


      // ── Cash Withdrawal ──────────────────────────────────────────────────
      if (formData.txnType === "CW") {
        const response = await cashWithdrawalApi({
          ...baseRequest,
          transactionType: "CW",
          amount: String(formData.amount),
        });

        if (response?.status === "000") {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Cash Withdrawal Success",
            message:
              `Amount : ₹${response?.transactionAmount}

Avl Balance: ₹${response?.availableBalance}

Bank RRN : ${response?.rrn}
Aadhaar No : ${response?.aadhaarNo}
Date : ${response?.txnDateTime}`
          });
        } else {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Failed",
            message: response?.npciMessage
          });
        }
      }


      // ── Aadhaar Pay ───────────────────────────────────────────────────────
      if (formData.txnType === "AP") {
        const response = await aadhaarPayApi({
          ...baseRequest,
          transactionType: "AP",
          amount: String(formData.amount),
        });

        if (response?.status === "000") {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Aadhaar Pay Success",
            message:
              `Amount : ₹${response?.transactionAmount}

Avl Balance: ₹${response?.availableBalance}

Bank RRN : ${response?.rrn}
Aadhaar No : ${response?.aadhaarNo}
Date : ${response?.txnDateTime}`
          });
        } else {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Failed",
            message: response?.npciMessage
          });
        }
      }


      // ── Mini Statement ────────────────────────────────────────────────────
      if (formData.txnType === "MS") {
        const response = await miniStatementApi({
          ...baseRequest,
          transactionType: "MS",
          amount: "0",
        });

        if (response?.status === "000") {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Mini Statement",
            message: "",
            transactions: parseMiniStatement(response?.transactionList),
            balance: response?.availableBalance || "0",
            aadhaarNo: response?.aadhaarNo || "",
            txnRefId: response?.txnRefId || "",
          });
        } else {
          setProcessing(false);
          setPopup({
            show: true,
            title: "Failed",
            message: response?.npciMessage,
            transactions: null,
            balance: "",
          });
        }
      }

    } catch (err) {
      setProcessing(false);
      setPopup({
        show: true,
        title: "Error",
        message: err?.message || "Transaction Failed"
      });
    }
  };


  return (
    <div>

      <DeviceStatus device={device} />

      <VimoAepsForm
        onSubmit={handleCapture}
        processing={processing}
        loading={loading}
        previousData={previousData}
        clearFormTrigger={clearFormTrigger}
      />


      {/* OTP Popup — shown when CW/AP > ₹5,000 */}
      {otpPopup.show && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-8 w-[420px] text-center">

            <h2 className="text-2xl font-semibold">Enter OTP</h2>

            <p className="mt-3 text-[15px] text-gray-500 leading-6">
              An OTP has been sent to the customer's Aadhaar-registered mobile number.
              Enter the OTP below, then place the finger on the scanner.
            </p>

            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpPopup.otp}
              onChange={(e) =>
                setOtpPopup(p => ({ ...p, otp: e.target.value.replace(/\D/g, "") }))
              }
              placeholder="• • • • • •"
              className="mt-6 w-full border-2 border-gray-300 rounded-xl px-4 py-4 text-3xl tracking-[0.5em] text-center font-mono focus:outline-none focus:border-blue-500"
            />

            <div className="mt-6 flex gap-3">
              <button
                onClick={() =>
                  setOtpPopup({ show: false, otp: "", cwAuthTxnId: "", pendingFormData: null, submitting: false })
                }
                disabled={otpPopup.submitting}
                className="flex-1 border-2 border-gray-300 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleOtpSubmit}
                disabled={otpPopup.otp.length < 4 || otpPopup.submitting}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold disabled:opacity-50"
              >
                {otpPopup.submitting ? "Processing…" : "Submit & Scan"}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Result popup */}
      {popup.show && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-3xl shadow-2xl p-6 text-center ${popup.transactions ? "w-full max-w-lg" : "w-[420px]"}`}>

            <h2 className="text-2xl font-semibold">{popup.title}</h2>

            {popup.transactions ? (
              /* ── Mini Statement table ── */
              <div className="mt-4 text-left">
                <div className="flex items-center justify-between bg-blue-50 rounded-xl px-4 py-3 mb-2">
                  <span className="text-sm text-gray-500 font-medium">Available Balance</span>
                  <span className="text-lg font-bold text-blue-700">₹{popup.balance}</span>
                </div>
                {popup.aadhaarNo && (
                  <div className="flex items-center justify-between px-4 py-1.5 mb-1">
                    <span className="text-xs text-gray-400">Aadhaar No</span>
                    <span className="text-xs font-medium text-gray-700 tracking-wider">{popup.aadhaarNo}</span>
                  </div>
                )}
                {popup.txnRefId && (
                  <div className="flex items-center justify-between px-4 py-1.5 mb-3">
                    <span className="text-xs text-gray-400">Txn Ref ID</span>
                    <span className="text-xs font-medium text-gray-700 font-mono break-all text-right max-w-[220px]">{popup.txnRefId}</span>
                  </div>
                )}

                {popup.transactions.length === 0 ? (
                  <p className="text-center text-gray-400 py-6">No transactions found</p>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {popup.transactions.map((txn, i) => (
                      <div
                        key={i}
                        className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${txn.type === "CR"
                            ? "bg-green-50 border-green-100"
                            : "bg-red-50 border-red-100"
                          }`}
                      >
                        {/* CR / DR badge */}
                        <span
                          className={`mt-0.5 shrink-0 text-xs font-bold px-2 py-1 rounded-md ${txn.type === "CR"
                              ? "bg-green-600 text-white"
                              : "bg-red-600 text-white"
                            }`}
                        >
                          {txn.type || "—"}
                        </span>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {txn.narration || "Transaction"}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {txn.date} {txn.time}
                          </p>
                        </div>

                        <span
                          className={`shrink-0 font-bold text-base ${txn.type === "CR" ? "text-green-700" : "text-red-700"
                            }`}
                        >
                          {txn.type === "CR" ? "+" : "-"}₹{txn.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-5 whitespace-pre-line text-[18px] font-bold text-gray-700 leading-8">
                {popup.message}
              </p>
            )}

            <button
              onClick={closePopup}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-10 py-3 rounded-xl"
            >
              OK
            </button>

          </div>
        </div>
      )}

    </div>
  );
};

export default VimoAepsIntegration;
